// games/Hangman.jsx
// FSM-aware. Cooperative mode. UUID-safe player comparison.
// Timer fix: useEffect deps use stable primitives so countdown doesn't reset on every poll.
// Replay: 5-second auto-countdown after game ends, then auto-starts new round.

import { useState, useEffect, useRef } from 'react';
import { useGameSession } from '../useGameSession';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const CATEGORY_LABELS = {
  bollywood: '🎬 Bollywood', english_songs: '🎵 Songs',
  animals: '🐾 Animals', food: '🍜 Food', places: '🌍 Places', brands: '🏷️ Brands', sports: '⚽ Sports',
};
const TURN_TIMER   = 15;
const REPLAY_TIMER = 5;

export default function Hangman({ sessionId, myPlayerId, onBack, onRestart }) {
  const { session, loading, error, submitMove, submitSkip, confirmReady, resign, restart } = useGameSession(sessionId);
  const [submitting,      setSubmitting]      = useState(false);
  const [tick,            setTick]            = useState(0);
  const [replayLeft,      setReplayLeft]      = useState(REPLAY_TIMER);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const timerRef        = useRef(null);
  const replayRef       = useRef(null);
  const iAmInvitedByRef = useRef(false); // ref so interval callbacks always read current value

  // ── Server-anchored turn timer ───────────────────────────────────────────────
  // timeLeft is derived from session.lastMoveAt on each render tick.
  // Both players compute identical values from the same server timestamp.
  const fsmState    = session?.fsm_state;
  const currentTurn = session?.currentTurn;
  const moveCount   = session?.move_count;
  const lastMoveAt  = session?.lastMoveAt;
  const endReason   = session?.end_reason;
  const skipFiredRef = useRef(false);

  const timeLeft = (fsmState === 'ACTIVE' && lastMoveAt)
    ? Math.max(0, TURN_TIMER - Math.floor((Date.now() - new Date(lastMoveAt)) / 1000))
    : TURN_TIMER;

  // Drive re-renders every second while ACTIVE; reset skip guard on each new turn
  useEffect(() => {
    clearInterval(timerRef.current);
    if (fsmState !== 'ACTIVE') return;
    skipFiredRef.current = false;
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [fsmState, currentTurn, moveCount]);

  // Fire skip exactly once when timeLeft hits 0 — only the current-turn player fires
  useEffect(() => {
    if (fsmState !== 'ACTIVE') return;
    if (timeLeft > 0) return;
    if (String(currentTurn) !== String(myPlayerId)) return;
    if (skipFiredRef.current) return;
    skipFiredRef.current = true;
    clearInterval(timerRef.current);
    submitSkip();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, fsmState, currentTurn]);

  // ── Keep iAmInvitedByRef current whenever session loads or myPlayerId changes ─
  // This prevents stale closure bugs in interval callbacks.
  const invitedBy     = session?.invited_by;
  const nextSessionId = session?.nextSessionId;
  if (fsmState === 'COMPLETED') console.log('[P2-TRACE] fsmState=COMPLETED nextSessionId=', nextSessionId, 'iAmInvitedBy=', String(invitedBy) === String(myPlayerId));

iAmInvitedByRef.current = String(invitedBy) === String(myPlayerId);

  // ── Auto-replay countdown ───────────────────────────────────────────────────
  // Only invited_by fires /restart when countdown hits zero.
  // invited_player watches nextSessionId and transitions via onRestart.
  useEffect(() => {
    clearInterval(replayRef.current);
    if (fsmState !== 'COMPLETED') return;
    if (endReason === 'resign') return; // player left — no auto-restart
    setReplayLeft(REPLAY_TIMER);
    replayRef.current = setInterval(() => {
      setReplayLeft(t => {
        if (t <= 1) {
          clearInterval(replayRef.current);
          if (iAmInvitedByRef.current) handleRestart('extend'); // ref — always current
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(replayRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsmState]);

  // ── Non-restarting player watches nextSessionId ─────────────────────────────
  useEffect(() => {
    if (fsmState !== 'COMPLETED') return;
    if (endReason === 'resign') return; // player left — no auto-restart
    if (!nextSessionId) return;
    if (iAmInvitedByRef.current) return; // invited_by handles own transition
    clearInterval(replayRef.current);
      if (onRestart) onRestart(nextSessionId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsmState, nextSessionId]);

  if (loading) return <div className="hm-msg">Connecting…</div>;
  if (error)   return <div className="hm-msg err">Connection error. Retrying…</div>;
  if (!session) return null;

  const { fsm_state, game_state: gs, ready_confirmed } = session;
  const isMyTurn = String(session.currentTurn) === String(myPlayerId);

  const myScore  = gs?.scores?.[String(myPlayerId)]  ?? 0;
  const oppScore = Object.entries(gs?.scores ?? {})
    .find(([id]) => String(id) !== String(myPlayerId))?.[1] ?? 0;

  const iHaveConfirmed = !!ready_confirmed?.[String(myPlayerId)];

  const displayWord = gs?.word
    ?.split('').map(l => l === ' ' ? '/' : gs.revealed?.includes(l) ? l : '_').join('  ') ?? '';

  const livesLeft = (gs?.maxWrong ?? 6) - (gs?.wrongGuesses ?? 0);

  async function handleLetter(letter) {
    if (!isMyTurn || submitting) return;
    setSubmitting(true);
    await submitMove(myPlayerId, { type: 'letter', letter });
    setSubmitting(false);
  }

  async function handleRestart(mode) {
    clearInterval(replayRef.current);
    const newSessionId = await restart('hangman', mode);
    // restart() returns the sessionId string directly (new or reused)
    if (newSessionId && onRestart) {
      onRestart(newSessionId);
    }
  }

async function handleLeaveConfirmed() {
    clearInterval(replayRef.current);
    clearInterval(timerRef.current);
    setShowLeaveDialog(false);
    // Fire resign directly via fetch — cannot use resign() hook here because
    // onBack() unmounts this component (and the hook) before the await completes,
    // silently aborting the in-flight request.
    try {
      const token = localStorage.getItem('binko_token');
      await fetch(`${process.env.REACT_APP_API_BASE || 'https://api.binko.me/api'}/games/${sessionId}/resign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
    } catch (e) { /* non-fatal — server will idle-rule the session */ }
    if (onBack) onBack();
  }

  // ── READY_PENDING ───────────────────────────────────────────────────────────
  if (fsm_state === 'READY_PENDING') {
    return (
      <>
        <style>{HM_STYLES}</style>
        <div className="hm-phase">
          <div className="hm-icon">✋</div>
          <h3>Ready to play Hangman?</h3>
          <p className="hm-sub">Both players must confirm within 10 seconds.</p>
          {!iHaveConfirmed
            ? <button className="hm-btn" onClick={() => confirmReady()}>I'm Ready</button>
            : <p className="hm-waiting">Waiting for the other player…</p>
          }
          <button className="hm-resign" onClick={() => { resign(); if (onBack) onBack(); }}>Not now</button>
        </div>
      </>
    );
  }

 // ── CANCELLED ───────────────────────────────────────────────────────────────
  if (fsm_state === 'CANCELLED') {
    return (
      <>
        <style>{HM_STYLES}</style>
        <div className="hm-phase">
          <div className="hm-icon">❌</div>
          <h3>Game cancelled</h3>
          <p>
            {session.cancel_reason === 'ready_timeout'
              ? "A player wasn't ready in time — please retry"
              : session.cancel_reason === 'resign_before'
              ? "A player left before the game started"
              : (session.cancel_reason || 'Game was cancelled').replace(/_/g, ' ')}
          </p>
          <button className="hm-btn" onClick={() => { if (onBack) onBack(); }}>Try Another Game</button>
        </div>
      </>
    );
  }

  // ── COMPLETED ───────────────────────────────────────────────────────────────
  if (fsm_state === 'COMPLETED') {
    const won = gs?.won || (gs?.word && gs?.revealed && gs.word.split('').every(l => gs.revealed.includes(l)));
    const leftByPlayer = session.end_reason === 'resign';

    return (
      <>
        <style>{HM_STYLES}</style>
        <div className="hm-phase">
          <div className="hm-icon">{leftByPlayer ? '👋' : won ? '🎉' : '💀'}</div>
          <h3>{leftByPlayer ? 'A player left' : won ? 'You cracked it together!' : 'Game over'}</h3>
          {leftByPlayer && <p className="hm-sub">A player has left the game.</p>}
          <p className="hm-reveal">The word was <strong>{gs?.word}</strong></p>
          <div className="hm-final-scores">
            <div className="hm-score-item"><span className="lbl">You</span><span className="val">{myScore} pts</span></div>
            <div className="hm-score-sep">vs</div>
            <div className="hm-score-item"><span className="lbl">Them</span><span className="val">{oppScore} pts</span></div>
          </div>

          {/* Auto-replay countdown — hidden when a player resigned */}
          {!leftByPlayer && <p className="hm-replay-countdown">Next round in <strong>{replayLeft}s</strong>…</p>}
          {leftByPlayer && (
            <button className="hm-btn" onClick={handleLeaveConfirmed}>Exit</button>
          )}
        </div>

        {/* Leave dialog */}
        {showLeaveDialog && (
          <div className="hm-overlay">
            <div className="hm-dialog">
              <p>You sure?</p>
              <button className="hm-btn" onClick={() => setShowLeaveDialog(false)}>Cancel &amp; continue</button>
              <button className="hm-resign" onClick={handleLeaveConfirmed}>End the game</button>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── ACTIVE ───────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{HM_STYLES}</style>
      <div className="hm-wrap">
        <div className="hm-scores">
          <div className={`hm-score ${isMyTurn ? 'active' : ''}`}>
            <span className="lbl">You</span>
            <span className="val">{myScore}</span>
          </div>
          <div className="hm-coop-label">working together</div>
          <div className={`hm-score ${!isMyTurn ? 'active' : ''}`}>
            <span className="lbl">Them</span>
            <span className="val">{oppScore}</span>
          </div>
        </div>

        <div className="hm-card">
          <div className="hm-meta">
            <span className="hm-cat">{CATEGORY_LABELS[gs?.category] ?? gs?.category}</span>
            <div className={`hm-timer ${timeLeft <= 4 ? 'danger' : ''}`}>{timeLeft}</div>
          </div>

          <div className="hm-word">{displayWord}</div>

          <div className="hm-dots">
            {Array.from({ length: gs?.maxWrong ?? 6 }).map((_, i) => (
              <div key={i} className={`hm-dot ${i < (gs?.wrongGuesses ?? 0) ? 'used' : 'ok'}`} />
            ))}
          </div>
          <div className="hm-wrong-lbl">{livesLeft} {livesLeft === 1 ? 'life' : 'lives'} left</div>

          <div className={`hm-turn-lbl ${isMyTurn ? '' : 'waiting'}`}>
            {isMyTurn ? 'Your turn — pick a letter' : 'Waiting for their move…'}
          </div>
        </div>

        <div className="hm-alpha">
          {ALPHABET.map(l => (
            <button
              key={l} className="hm-letter"
              disabled={!isMyTurn || gs?.revealed?.includes(l) || submitting}
              onClick={() => handleLetter(l)}
            >{l}</button>
          ))}
        </div>

        <button className="hm-resign" onClick={() => setShowLeaveDialog(true)}>Leave Game</button>
      </div>

      {/* Leave dialog */}
      {showLeaveDialog && (
        <div className="hm-overlay">
          <div className="hm-dialog">
            <p>You sure?</p>
            <button className="hm-btn" onClick={() => setShowLeaveDialog(false)}>Cancel &amp; continue</button>
            <button className="hm-resign" onClick={handleLeaveConfirmed}>End the game</button>
          </div>
        </div>
      )}
    </>
  );
}

const HM_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@400;600&display=swap');
  .hm-wrap { font-family:'DM Sans',sans-serif; max-width:420px; margin:0 auto; padding:16px; }
  .hm-msg  { text-align:center; padding:40px; color:#8b949e; font-size:0.85rem; }
  .hm-msg.err { color:#f43f5e; }

  .hm-phase { background:#1a0f07; border:1px solid #3d2510; border-radius:16px;
    padding:28px 20px; text-align:center; display:flex; flex-direction:column;
    align-items:center; gap:12px; }
  .hm-icon  { font-size:2.4rem; }
  .hm-phase h3 { font-family:'Playfair Display',serif; color:#f5d895; font-size:1.2rem; margin:0; }
  .hm-phase p  { font-size:0.82rem; color:#a07850; margin:0; }
  .hm-waiting  { font-size:0.78rem; color:#6b5060; animation:hm-pulse 1.4s ease-in-out infinite; }
  @keyframes hm-pulse { 0%,100%{opacity:1}50%{opacity:0.35} }
  .hm-sub      { font-size:0.78rem; color:#a07850; }
  .hm-replay-countdown { font-size:0.78rem; color:#6b5060; margin:0; }
  .hm-replay-countdown strong { color:#f5a623; }

  .hm-scores { display:flex; justify-content:space-between; align-items:center;
    background:#1a1208; border:1px solid #3d2510; border-radius:12px;
    padding:12px 20px; margin-bottom:14px; }
  .hm-score      { text-align:center; }
  .hm-score .lbl { display:block; font-size:0.65rem; color:#a07850; text-transform:uppercase; letter-spacing:0.1em; }
  .hm-score .val { display:block; font-size:1.4rem; font-weight:700; color:#f5d895; font-family:'Playfair Display',serif; }
  .hm-score.active .lbl { color:#f5a623; }
  .hm-score.active .val { color:#f5a623; }
  .hm-coop-label { font-size:0.6rem; color:#6b5060; text-transform:uppercase; letter-spacing:0.08em; }

  .hm-card { background:#1a0f07; border:1px solid #3d2510; border-radius:16px; padding:20px; margin-bottom:12px; }
  .hm-meta { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
  .hm-cat  { font-size:0.72rem; color:#a07850; background:#2a1a0e; border:1px solid #3d2510; padding:4px 10px; border-radius:20px; }
  .hm-timer { font-family:'Playfair Display',serif; font-size:1.3rem; font-weight:700; color:#f5d895;
    width:38px; height:38px; border-radius:50%; border:2px solid #3d2510;
    display:flex; align-items:center; justify-content:center; transition:all 0.3s; }
  .hm-timer.danger { border-color:#c0392b; color:#e74c3c; }

  .hm-word { text-align:center; font-family:'Playfair Display',serif;
    font-size:2rem; letter-spacing:0.2em; color:#f5d895; margin-bottom:14px; min-height:50px; }

  .hm-dots { display:flex; justify-content:center; gap:6px; margin-bottom:4px; }
  .hm-dot  { width:10px; height:10px; border-radius:50%; }
  .hm-dot.ok   { background:#f5a623; }
  .hm-dot.used { background:#c0392b; }
  .hm-wrong-lbl { text-align:center; font-size:0.68rem; color:#a07850; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:4px; }

  .hm-turn-lbl { text-align:center; margin-top:10px; font-size:1rem; font-weight:600; color:#f5a623; }
  .hm-turn-lbl.waiting { color:#6b5060; }

  .hm-alpha { display:flex; flex-wrap:wrap; gap:7px; justify-content:center; margin-bottom:12px; }
  .hm-letter { width:38px; height:38px; border-radius:8px; border:1px solid #3d2510;
    background:#2a1a0e; color:#f5d895; font-size:0.85rem; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .hm-letter:hover:not(:disabled) { background:#f5a623; color:#1a0f07; border-color:#f5a623; }
  .hm-letter:disabled { opacity:0.2; cursor:default; }

  .hm-btn { background:#f5a623; color:#1a0f07; border:none; border-radius:10px;
    padding:12px 28px; font-weight:700; font-size:0.88rem; cursor:pointer; width:100%; }
  .hm-btn:hover { background:#f5d895; }
  .hm-resign { display:block; width:100%; background:transparent; color:#6b5060;
    border:1px solid #3d2510; border-radius:10px; padding:9px; font-size:0.8rem; cursor:pointer; margin-top:6px; }
  .hm-resign:hover { color:#f43f5e; border-color:#f43f5e; }

  .hm-reveal { font-size:0.85rem; color:#a07850; }
  .hm-reveal strong { color:#f5d895; }
  .hm-final-scores { display:flex; gap:16px; align-items:center; justify-content:center;
    background:#2a1a0e; border-radius:10px; padding:12px 20px; width:100%; }
  .hm-score-item { text-align:center; }
  .hm-score-item .lbl { display:block; font-size:0.62rem; color:#a07850; text-transform:uppercase; }
  .hm-score-item .val { font-size:1.1rem; font-weight:700; color:#f5a623; }
  .hm-score-sep { font-size:0.7rem; color:#6b5060; }
  .hm-end-reason { font-size:0.7rem; color:#6b5060; text-transform:uppercase; letter-spacing:0.08em; }

  .hm-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); display:flex;
    align-items:center; justify-content:center; z-index:1000; }
  .hm-dialog { background:#1a0f07; border:1px solid #3d2510; border-radius:16px;
    padding:28px 24px; text-align:center; display:flex; flex-direction:column; gap:12px;
    width:280px; }
  .hm-dialog p { font-family:'Playfair Display',serif; color:#f5d895; font-size:1.1rem; margin:0; }
`;
